performance.mark("shell-start");
import { ScopedStyleSheet } from "@nectary/scoped-stylesheet";
import { type MFE } from "@sinch/mfe-loader";
import { render } from "react-dom";
import { ShellRenderer } from "./ShellRenderer";

import "regenerator-runtime/runtime";
import { setupAuth } from "./auth";
import { loadConfig } from "./config";
import { setPortalConfig } from "./config/portalConfig";
import { setMFELoaderRegistrySafe } from "./helpers/mfe-loader";
import { setupNectary } from "./nectary-setup";
import { patchFetchWithCacheLayer } from "./patchFetchWithCache";
import { getThemeClassNames, loadThemes } from "./style/loadThemes";

const mount: MFE = (element, { createAppRoot }) => {
  performance.mark("shell-mount-start");
  element.id = "shell";

  (async () => {
    try {
      const configPromise = loadConfig();

      // Nectary Setup
      const customRegistry = window.SHELL_ENV.WITHOUT_SHADOW_ROOT
        ? window.customElements
        : new CustomElementRegistry();
      window.shellCustomRegistry = customRegistry;
      setMFELoaderRegistrySafe(customRegistry);
      setupNectary(customRegistry);

      const { shadowRoot, appElement } = createAppRoot(customRegistry, {
        appName: "Shell",
        framework: "react",
      });

      appElement.style.height = "100%";

      if (shadowRoot) {
        // Some MFEs uses library (e.g. Material UI) that needs this to render modals properly
        Object.defineProperty(shadowRoot, "defaultView", { value: window });
      }
      const scopedStyleSheet = new ScopedStyleSheet(
        // Shell needs to support loadding styles to both shadowRoot and head
        shadowRoot || (document.head as unknown as ShadowRoot)
      );

      const config = await configPromise;

      patchFetchWithCacheLayer();

      await setupAuth(
        config.AUTH_CONFIG.type,
        config.universalBootstrapOptions.patchRequestsWithAuth
      );

      const { extraNectaryTheme } = config.universalBootstrapOptions;

      const classNames = getThemeClassNames(
        extraNectaryTheme,
        config.VENDOR_LABEL
      );

      setPortalConfig("themeClassNames", classNames);

      await loadThemes(scopedStyleSheet, classNames);

      appElement.className = classNames;

      performance.mark("shell-mount-end");
      render(
        <ShellRenderer config={config} shadowRoot={shadowRoot} />,
        appElement
      );
    } catch (err) {
      const { loadCrashPage } = await import("./loadCrashPage");
      console.error(err);
      await loadCrashPage(err);
    }
  })();

  return () => undefined;
};

// eslint-disable-next-line no-restricted-syntax
export default mount;
